import _ from 'lodash';
import { makeAutoObservable, observable, runInAction } from 'mobx';
import moment from 'moment';
import { cardFromScrollToView, checkCardFromRule } from '../../../component/card/card-from';
import GlobalNotification from '../../../component/common/snackbar';
import BASE_CHECK_TYPE, { COUNTRY_REG_CITY_REGULAR, COUNTRY_REG_NAME_REGULAR, COUNTRY_REG_POSTCODE_REGULAR, COUNTRY_VAT_REGULAR } from '../../../constants/regular.constant';
import { defaultQopWanConfig, generateText, getMomentTimeZone, getTextTemplate, getTextTemplateQOP, getTextTemplateQOPEFR, getLanguageByCountry } from '../../../constants/tool.fn';
import i18n from '../../../i18n';
import { CoreFileLog, DatabasePortItem, DatabasePortValue, DatabaseTunnelProject, FiskalyClientInfo, Group, GybDevice, Restaurant, SecretKey, Staff } from '../../../models';
import apiManage, { apiManage_rbp } from '../../../request';
import { groupUnitData } from '../group-unit';
import { RestaurantInfoCardConfig } from './restaurant-info';
import { getResellerList } from '../../../constants/tool.reseller';
import { Option } from '../../../component/form/select-input-multiple';

/** 隧道生成目标模式。 */
type TunnelGenerationMode = 'single' | 'multi';

// 保存前需要清理首尾空格的地址字段，限制在餐厅信息页地址语义内。
const RESTAURANT_ADDRESS_TRIM_KEYS: ReadonlyArray<keyof Restaurant> = [
    'local',
    'home_umber',
    'postcode',
    'city',
    'state',
    'country',
    'reg_local',
    'reg_additional_address',
    'reg_postcode',
    'reg_city',
    'reg_state',
    'reg_country',
    'reg_municipality',
    'reg_street_number',
    'corp_local',
    'corp_additional_address',
    'corp_postcode',
    'corp_city',
    'corp_state',
    'corp_country',
    'corp_municipality',
    'corp_street_number'
];

export class RestaurantInfoState implements IRestaurantInfoState {

    constructor(props: Props) {
        makeAutoObservable(this);
        // 获取国家时区
        this.getTimeZoneNames();
        this.componentType = props.componentType;
        this.groupId = groupUnitData.belongedGroup;
        // this.belongedGroupId = groupUnitData?.belongedGroupId;
        //  餐厅 _id
        this.restaurantId = props?.restaurantId || '';
        this.initEditRestaurantData();
    }

    // *********************
    // Attributes
    // *********************

    // 组件类型
    componentType: 'ADD' | 'EDIT' | 'PREVIEW' | 'BASE_INFO' | undefined = 'ADD';
    // 集团stringId
    groupId: string | undefined = '';
    // 集团Id
    belongedGroupId: string | undefined = '';
    // 所有集团数据
    groupAllData: Group[] = [];
    // GYB列表
    GYBList: GybDevice[] = [];
    // 餐厅信息
    restaurantInfo: Restaurant = {};
    // 国家时区
    countryTimeZone: COUNTRYTIMEZONE[] = [];
    // 国家和地区
    countryCodeList: Array<Option & { item: any }> = []
    // 确认弹窗
    confirmDialogData: {
        visible: boolean;
        title: string;
        content: string;
        forbidConfirm?: number;
        handleClick?: (() => void) | (() => Promise<void>);
        onCancel?: () => void;
        onClose?: () => void;
        cancelText?: string;
        confirmText?: string;
    } = {
            visible: false,
            title: '',
            content: '',
            forbidConfirm: 0
        }
    // 搜索key
    selectValue: string = 'nickname';
    // 显示弹窗 - 密钥
    showExtensionsDialog: {
        open: boolean;
        onClose: () => void;
        secretKey: SecretKey;
        dialogType: 'ADD' | 'EDIT';
        onSave?: (params: { name: string }) => Promise<void>;
    } = {
            open: false,
            onClose: () => { },
            secretKey: {},
            dialogType: 'ADD',
        };
    // 显示弹窗 - 核心文件日志
    showCoreFlieLogDialog: {
        open: boolean;
        onClose: () => void;
        dialogType: 'PREVIEW';
        onSave?: (params: { name: string }) => void;
        info?: CoreFileLog;
    } = {
            open: false,
            onClose: () => { },
            dialogType: 'PREVIEW',
        };
    // 显示弹窗 - GYB
    showGYBDialog: {
        open: boolean;
        onClose: () => void;
        onSaveCallback: (error: boolean) => void;
        dialogType: 'ADD' | 'PREVIEW';
        GYBDeviceInfo?: GybDevice;
    } = {
            open: false,
            onClose: () => { },
            onSaveCallback: () => { },
            dialogType: 'ADD',
        };
    // 显示弹窗 - 预览协议
    showPreviewAgreementDialog: {
        open: boolean;
        onClose: () => void;
        url: string;
    } = {
            open: false,
            onClose: () => { },
            url: ''
        }
    // 是否允许编辑法人信息
    isDisabledLegalPerson: boolean = false;
    // 是否点击了编辑法人弹窗
    isEditLegalPerson: boolean = false;
    // 餐厅版本信息
    restaurantVersions: {
        value: string;
        label: string;
    }[] = [{
        value: '2.0',
        label: i18n.t('restaurant_page_input_text_restaurant_x_version', { x: '2.0' })
    },
    {
        value: '2.5',
        label: i18n.t('restaurant_page_input_text_restaurant_x_version', { x: '2.5' })
    }];
    authorizationTypes: {
        value: string;
        label: string;
    }[] = [
            { value: 'Mod.1', label: 'Mod.1' },
            { value: 'SaaS', label: 'SaaS' },
            { value: 'Mix Mode', label: 'Mix Mode' }
        ]
    businessLabels: {
        value: string;
        label: string;
    }[] = [
            { value: 'POS', label: 'POS' },
            { value: 'OD', label: 'OD' },
            { value: 'Site', label: 'Site' },
            { value: 'MKT', label: 'MKT' },
        ];
    // 经销商列表    
    resellerList: {
        value: string;
        label: string;
    }[] = [];
    // 隧道项目选项
    tunnelProjectOptions: {
        value: DatabaseTunnelProject;
        label: DatabaseTunnelProject;
    }[] = [
            { value: 'ECCO', label: 'ECCO' },
            { value: 'EFR', label: 'EFR' }
        ];
    // 餐厅_id
    restaurantId: string = '';
    // 编辑餐厅信息
    editRestaurantData: Restaurant = {};
    // 编辑输入框操作map
    @observable operateMap: Map<string, { origin: unknown, current: unknown }> = new Map();
    // 餐厅头像
    editRestaurantDataAvatar: {
        src: string;
        file?: File;
    } = {
            src: ''
        };
    // 负责人信息弹窗配置
    responsabileDialogData: {
        // 打开弹窗
        visible: boolean;
        // 弹窗标题
        title?: string;
        // 关闭弹窗
        onClose?: () => void;
    } = {
            visible: false,
            title: '',
            onClose: () => { },
        }
    // 文件日志
    coreFileLog: CoreFileLog[] = [];
    // 允许空校验 - 输入框校验
    allowNullVerification: boolean = false;
    // 校验下载隧道文件时城市信息是否填写
    validateCityRequired: boolean = false;
    // qop wan warrant AU1024 
    QOP_WAN_WARRANT: boolean = false;
    // 是否主动编辑过模块折扣
    isEditModuleDiscount: boolean = false;
    // 是否主动编辑过年费折扣
    isEditYearDiscount: boolean = false;

    // *********************
    // Function
    // *********************

    async init() {
        this.initEditRestaurantData();
        // 获取集团数据
        this.getAllGroupData();
        // 获取经销商数据
        this.getAllResellerData();
        // 国家和地区
        this.getCountryLongNameList();
        if (this.componentType !== 'ADD' && this.restaurantId) {
            // 获取餐厅信息
            const info = this.getRestaurantInfo(this.restaurantId);
            // 获取餐厅logo
            const logo = this.getRestaurantLogo(this.restaurantId);
            // 获取餐厅GYB
            const gyb = this.getRestaurantGYB(this.restaurantId);
            await Promise.all([info, logo, gyb]);

            // 核心文件日志 2.5餐厅
            if (this.restaurantInfo.restaurant_version === '2.5') {
                await this.getCoreFileLog()
            }
            // 校验QOP_WAN AU1024 授权点
            const res = await apiManage.check_warrant_code({ string_id: this.restaurantInfo.string_id!, code: 'AU1024' });
            if (res) {
                runInAction(() => {
                    this.QOP_WAN_WARRANT = !!res.passedWarrant?.length;
                    if (this.QOP_WAN_WARRANT) {
                        // 自动填充qop wan url
                        this.updateEditRestaurantData(defaultQopWanConfig({ type: 'url', string_id: this.restaurantInfo.string_id }), 'url');
                    }
                });
                return
            }
        }
    }

    // 初始化表单数据
    initEditRestaurantData() {
        this.editRestaurantDataAvatar = { src: '' };
        this.restaurantInfo = {};
        this.editRestaurantData = {
            time_zone: 'Asia/Shanghai',
            restaurant_version: '2.0',
            belonged_group: this.groupId,
            belonged_reseller: 'Admin',
            nickname: '',
            setting_restaurant_operating_status: '1',
            description: '',
            email: '',
            tel_prefix: '39',
            tel: '',
            website: '',
            restaurant_contact_person: '',
            local: '',
            home_umber: '',
            postcode: '',
            city: '',
            state: '',
            country: '',
            reg_name: '',
            reg_fiscal_code: '',
            reg_vat_number: '',
            reg_pec: '',
            reg_des_code: '',
            reg_local: '',
            reg_additional_address: '',
            reg_postcode: '',
            reg_city: '',
            reg_state: '',
            reg_country: '',
            database_host: '',
            // 文本框需要字符串
            database_port: '' as unknown as DatabasePortValue,
            Aruba_username: '',
            Aruba_password: '',
            plateform_api_user: '',
            plateform_api_password: '',
            business_labels: [],
            authorization_type: '',
            discount_info: '',
            discount_annual: '',
            accountant_name: '',
            accountant_phone: '',
            accountant_phone_prefix: '39',
            customer_mark: '',
            head: 'http',
            url: '',
            port: '80',
            country_code: '+39'
        };
        this.allowNullVerification = false;
        this.operateMap = new Map();
        this.validateCityRequired = false;
        this.QOP_WAN_WARRANT = false;
        this.isEditModuleDiscount = false;
        this.isEditYearDiscount = false;
        this.isEditLegalPerson = false;
    }

    updateAllowNullVerification(val: boolean) {
        this.allowNullVerification = val;
    }

    /**
     * 密钥弹窗
     */
    updateExtensionsDialog(params: {
        open: boolean;
        secretKey?: SecretKey;
        dialogType?: 'ADD' | 'EDIT';
        onSave?: (params: { name: string }) => Promise<void>;
    }) {
        const key = (this.restaurantInfo?.secret_key || []).find((item) => { return item._id === params?.secretKey?._id }) || {};

        this.showExtensionsDialog = {
            open: params.open,
            onClose: () => { return this.updateExtensionsDialog({ ...this.showExtensionsDialog, open: false }) },
            secretKey: key,
            dialogType: params.dialogType || 'ADD',
            onSave: params.onSave
        };
    }
    /**
     * 密钥弹窗
     */
    updateCoreFlieLogDialog(params: {
        open: boolean;
        dialogType?: 'PREVIEW';
        onSave?: (params: { name: string }) => void;
        info?: CoreFileLog
    }) {

        this.showCoreFlieLogDialog = {
            open: params.open,
            onClose: () => { return this.updateCoreFlieLogDialog({ ...this.showCoreFlieLogDialog, open: false }) },
            dialogType: params.dialogType || 'PREVIEW',
            info: params.info,
            onSave: params.onSave
        };
    }
    /**
     * 确认弹窗
     */
    updateConfirmDialog(params: {
        visible: boolean;
        title?: string;
        content?: string;
        handleClick?: (() => Promise<void>) | (() => void);
        onCancel?: () => void;
        onClose?: () => void;
        cancelText?: string;
        confirmText?: string;
    }) {
        this.confirmDialogData = {
            onCancel: () => {
                if (params.onCancel) {
                    params.onCancel?.()
                } else {
                    this.updateConfirmDialog({ ...this.confirmDialogData, visible: false })
                }
            },
            visible: params.visible,
            title: params.title || '',
            content: params.content || '',
            handleClick: async () => await params.handleClick?.(),
            cancelText: params.cancelText || '',
            confirmText: params.confirmText || '',
            onClose: params.onClose
        };
    }
    /**
     * gyb弹窗
     */
    updateGYBDialog(params: {
        open: boolean;
        onClose?: () => void;
        onSaveCallback: (error: boolean) => void;
        dialogType: 'ADD' | 'PREVIEW';
        GYBDeviceInfo?: GybDevice;
    }) {
        this.showGYBDialog = {
            onClose: () => this.updateGYBDialog({ ...this.showGYBDialog, open: false }),
            open: params.open,
            onSaveCallback: params.onSaveCallback,
            dialogType: params.dialogType,
            GYBDeviceInfo: params.GYBDeviceInfo
        };
    }

    /**
     *  预览协议弹窗
     * @param params 
     */
    updatePreviewAgreementDialog(params: {
        open: boolean;
        url: string;
    }) {
        if (params.open && !params.url) {
            GlobalNotification.error(i18n.t('general_text_no_data'))
        }
        this.showPreviewAgreementDialog = {
            open: params.open,
            onClose: () => this.updatePreviewAgreementDialog({ ...this.showPreviewAgreementDialog, open: false }),
            url: params.url
        }
    }

    updateComponentType(componentType: typeof this.componentType) {
        this.componentType = componentType;
    }
    updateRestaurantId(restaurantId: string) {
        this.restaurantId = restaurantId;
    }
    /**
     * 获取国家时区
     */
    getTimeZoneNames(): void {
        const newCountryTimeZone: Array<COUNTRYTIMEZONE> = getMomentTimeZone()
        this.countryTimeZone = newCountryTimeZone;
    }
    // 获取所有集团数据
    async getAllGroupData() {
        const result = await apiManage.get_group_by_condition_V2({});
        if (!result?.error) {
            runInAction(() => {
                this.groupAllData = result?.[0]?.dbResults || [];
            })
        } else {
            GlobalNotification.error(`${i18n.t('group_management_page_text_failed_get_group_list')}`);
        }
    }
    // 获取所有国家地区列表
    async getCountryLongNameList() {
        const result = await apiManage_rbp.get_country();
        if (!result?.error) {
            runInAction(() => {
                this.countryCodeList = result.map((item) => ({
                    value: item.code || '',
                    label: item.name || '',
                    item
                }))
            })
        }
    }
    // 获取所有经销商数据
    async getAllResellerData() {
        const resellerList = await getResellerList(false);
        runInAction(() => {
            this.resellerList = resellerList;
        })
    }
    // 获取餐厅信息
    async getRestaurantInfo(restaurantId?: string): Promise<void> {
        try {
            const result: Restaurant & { error: true } = await apiManage.get_restaurant_info(restaurantId || this.restaurantId, this.componentType === 'PREVIEW' ? true : undefined);
            if (!result?.error) {
                runInAction(() => {
                    this.restaurantInfo = result;
                    this.editRestaurantData = {
                        time_zone: result.time_zone || '',
                        restaurant_version: result.restaurant_version || '',
                        string_id: result.string_id || '',
                        belonged_group: result.belonged_group,
                        belonged_reseller: result.belonged_reseller || 'Admin',
                        nickname: result.nickname || '',
                        setting_restaurant_operating_status: result.setting_restaurant_operating_status === '0' ? '1' : result.setting_restaurant_operating_status || '1',
                        ex_string_id: result.ex_string_id || '',
                        description: result.description || '',
                        email: result.email || '',
                        tel_prefix: result.tel_prefix || '39',
                        tel: result.tel || '',
                        website: result.website || '',
                        restaurant_contact_person: result.restaurant_contact_person || '',
                        local: result.local || '',
                        home_umber: result.home_umber || '',
                        postcode: result.postcode || '',
                        city: result.city || '',
                        state: result.state || '',
                        country: result.country || '',
                        reg_name: result.reg_name || '',
                        reg_fiscal_code: result.reg_fiscal_code || '',
                        reg_vat_number: result.reg_vat_number || '',
                        reg_pec: result.reg_pec || '',
                        reg_des_code: result.reg_des_code || '',
                        reg_local: result.reg_local || '',
                        reg_additional_address: result.reg_additional_address || '',
                        reg_postcode: result.reg_postcode || '',
                        reg_city: result.reg_city || '',
                        reg_state: result.reg_state || '',
                        reg_country: result.reg_country || '',
                        reg_municipality: result.reg_municipality || '',
                        reg_street_number: result.reg_street_number || '',
                        reg_territory: result.reg_territory || '',
                        database_host: result.database_host || '',
                        // 兼容旧版 number 与新版数组结构
                        database_port: this.normalizeDatabasePortValue(result.database_port) as DatabasePortValue,
                        // Aruba
                        Aruba_password: result.Aruba_password || '',
                        Aruba_username: result.Aruba_username || '',
                        activation_time: result.activation_time || '',
                        plateform_api_user: result.plateform_api_user || '',
                        plateform_api_password: result.plateform_api_password || '',
                        // 业务标签数组
                        business_labels: result?.business_labels || [],
                        // 授权方式
                        authorization_type: result?.authorization_type || '',
                        //模块折扣
                        discount_info: result.discount_info || '',
                        //年费折扣
                        discount_annual: result.discount_annual || result.discount_info || '',
                        //会计师信息
                        accountant_name: result.accountant_name || '',
                        // 会计师手机号
                        accountant_phone: result.accountant_phone || '',
                        // 会计师手机号前缀
                        accountant_phone_prefix: result.accountant_phone_prefix || '39',
                        // 客户备注
                        customer_mark: result?.customer_mark || '',
                        // qop WAN端口地址
                        head: result?.head || 'http',
                        // qop url 域名地址
                        url: result?.url || '',
                        // qop port 端口号
                        port: result?.port || '80',
                        // 国家和地区
                        country_code: result.country_code || '+39',
                        // fiskaly 相关信息
                        fiskaly_manage_organization_key: result.fiskaly_manage_organization_key || '',
                        fiskaly_manage_organization_secret: result.fiskaly_manage_organization_secret || '',
                        // TEST(false) or PRODUCTION(true) 默认 true
                        fiskaly_environment: typeof result.fiskaly_environment === 'boolean' ? result.fiskaly_environment : true,
                        // 个人税号时签名表可编辑的 activity_code（非必填）
                        fiskaly_activity_code: result.fiskaly_activity_code || '',
                        // 法人
                        corp_name: result.corp_name,
                        corp_vat_number: result.corp_vat_number,
                        corp_local: result.corp_local,
                        corp_additional_address: result.corp_additional_address || '',
                        corp_postcode: result.corp_postcode,
                        corp_city: result.corp_city,
                        corp_state: result.corp_state,
                        corp_country: result.corp_country,
                        corp_municipality: result.corp_municipality,
                        corp_street_number: result.corp_street_number
                    };
                    // 已存在法人信息并且生成了协议，不允许编辑
                    this.isDisabledLegalPerson = !!result.corp_name && !!result.restaurant_unsigned_agreement;
                })

            } else {
                GlobalNotification.error(i18n.t('restaurant_page_request_text_failed_to_get_restaurant_information'));
            }
        } catch (error) {
            GlobalNotification.error(i18n.t('restaurant_page_request_text_failed_to_get_restaurant_information'));
        }
    }
    // 获取头像
    async getRestaurantLogo(restaurantId: string): Promise<void> {
        if (restaurantId) {
            const result = await apiManage.get_restaurant_logo(restaurantId);
            if (!result?.error) {
                runInAction(() => {
                    this.editRestaurantDataAvatar = { src: result };
                })
            } else {
                GlobalNotification.error(i18n.t('restaurant_page_request_text_getting_restaurant_logo_failed'));
            }
        }
    }
    // 获取餐厅GYB
    async getRestaurantGYB(restaurantId: string): Promise<void> {
        const result = await apiManage.get_restaurant_gyb({ restaurant_id: restaurantId });
        if (!result?.error) {
            runInAction(() => {
                this.GYBList = result?.[0]?.dbResults || [];
            })
        }
    }

    /**
     * core file相关
     * - 2.5餐厅
     */
    async getCoreFileLog(page: number = 1) {
        const params = {
            restaurant_string_id: this.restaurantInfo.string_id || '',
            pagination_page: page,
            pagination_per_page: 9999
        }
        const result = await apiManage.get_restaurant_core_file_log(params);
        if (!result?.error) {
            const { dbResults } = result[0];
            this.coreFileLog = dbResults || [];
        } else {
            GlobalNotification.error(i18n.t('restaurant_page_message_failed_get_core_file_log'));
        }
    }

    /**
     * 判断当前值是否为双端口数组。
     * @param value 需要判断的端口值
     * @returns 是否为数组结构
     */
    isDatabasePortArray(value: Restaurant['database_port'] | string | undefined): value is DatabasePortItem[] {
        return Array.isArray(value);
    }

    /**
     * 获取双端口空模板，用于异常数据兜底。
     * @returns 固定的 ECCO/EFR 两项
     */
    getEmptyDatabasePortItems(): DatabasePortItem[] {
        return [
            { project: 'ECCO', port: '' },
            { project: 'EFR', port: '' }
        ];
    }

    /**
     * 标准化端口值，兼容 number、string 与 undefined。
     * @param port 原始端口值
     * @returns 可用于表单展示的端口值
     */
    normalizeDatabasePortItemPort(port: DatabasePortItem['port'] | undefined): number | string {
        if (port === 0 || typeof port === 'number') {
            return port;
        }
        return typeof port === 'string' ? port : '';
    }

    /**
     * 标准化接口返回的 database_port，避免异常数组影响界面。
     * @param value 接口返回的端口值
     * @returns 可直接写入编辑态的端口值
     */
    normalizeDatabasePortValue(value: Restaurant['database_port'] | undefined): Restaurant['database_port'] | '' {
        if (this.isDatabasePortArray(value)) {
            // 保留当前数组顺序，只在末尾补齐缺失项目，避免切换 project 时端口号“换位”。
            const normalizedItems: DatabasePortItem[] = [];
            const existedProjects = new Set<DatabaseTunnelProject>();
            value.forEach((item) => {
                if (!item || !['ECCO', 'EFR'].includes(item.project)) {
                    return;
                }
                const project = item.project as DatabaseTunnelProject;
                if (!existedProjects.has(project)) {
                    existedProjects.add(project);
                    normalizedItems.push({
                        project,
                        port: this.normalizeDatabasePortItemPort(item.port)
                    });
                }
            });
            this.tunnelProjectOptions.forEach((item) => {
                if (!existedProjects.has(item.value)) {
                    normalizedItems.push({
                        project: item.value,
                        port: ''
                    });
                }
            });
            return normalizedItems.slice(0, 2);
        }
        if (value === 0 || typeof value === 'number') {
            return value;
        }
        return '' as unknown as DatabasePortValue;
    }

    /**
     * 当前是否处于双端口模式。
     * @returns true 表示当前界面应显示 ECCO/EFR 双端口表单
     */
    get isMultiDatabasePortMode(): boolean {
        return this.isDatabasePortArray(this.editRestaurantData.database_port)
            || this.isDatabasePortArray(this.restaurantInfo.database_port);
    }

    /**
     * 获取双端口表单数据，保证始终返回 ECCO/EFR 两项。
     * @returns 双端口数组
     */
    get multiDatabasePortItems(): DatabasePortItem[] {
        if (this.isDatabasePortArray(this.editRestaurantData.database_port)) {
            return this.normalizeDatabasePortValue(this.editRestaurantData.database_port) as DatabasePortItem[];
        }
        return this.getEmptyDatabasePortItems();
    }

    /**
     * 获取单端口模式下的展示值，保持旧界面输入框不变。
     * @returns 单端口输入框值
     */
    get singleDatabasePortValue(): string | number {
        const { database_port } = this.editRestaurantData;
        if (this.isDatabasePortArray(database_port)) {
            const eccoItem = database_port.find((item) => item.project === 'ECCO');
            return this.normalizeDatabasePortItemPort(eccoItem?.port);
        }
        return (database_port as string | number | undefined) || '';
    }

    /**
     * 获取双端口项目下拉项。
     * @returns 下拉项列表
     */
    getDatabaseTunnelProjectOptions() {
        return this.tunnelProjectOptions;
    }

    /**
     * 更新双端口项目，并同步另一项为剩余项目。
     * @param index 当前编辑的下标
     * @param project 新项目值
     */
    updateMultiDatabasePortProject(index: number, project: DatabaseTunnelProject) {
        const nextItems = this.multiDatabasePortItems.map((item) => ({ ...item }));
        const otherIndex = index === 0 ? 1 : 0;
        const otherProject: DatabaseTunnelProject = project === 'ECCO' ? 'EFR' : 'ECCO';
        nextItems[index] = { ...nextItems[index], project };
        nextItems[otherIndex] = { ...nextItems[otherIndex], project: otherProject };
        this.updateEditRestaurantData(nextItems, 'database_port');
    }

    /**
     * 更新双端口端口号输入。
     * @param index 当前编辑的下标
     * @param value 输入框值
     */
    updateMultiDatabasePortPort(index: number, value: string) {
        const nextItems = this.multiDatabasePortItems.map((item) => ({ ...item }));
        nextItems[index] = {
            ...nextItems[index],
            port: value
        };
        this.updateEditRestaurantData(nextItems, 'database_port');
    }

    /**
     * 将 database_port 转为接口可提交的格式。
     * @param databasePort 当前端口值
     * @returns 接口入参使用的端口值
     */
    formatDatabasePortForSave(databasePort: Restaurant['database_port'] | string | undefined): Restaurant['database_port'] | undefined {
        if (this.isDatabasePortArray(databasePort)) {
            const normalizedItems = this.normalizeDatabasePortValue(databasePort) as DatabasePortItem[];
            return normalizedItems.map((item) => ({
                project: item.project,
                port: String(item.port).trim() === '' ? '' : Number(item.port)
            }));
        }
        if (databasePort === undefined || String(databasePort).trim() === '') {
            return undefined;
        }
        return typeof databasePort === 'number' ? databasePort : Number(databasePort);
    }

    /**
     * 同步本地隧道信息快照，避免自动保存后界面回显滞后。
     * @param params 最新隧道信息
     */
    syncTunnelSnapshot(params: Pick<Restaurant, 'database_host' | 'database_port' | 'head' | 'url' | 'port' | 'cloud_analysis_origin'>) {
        this.restaurantInfo = {
            ...this.restaurantInfo,
            ...params
        };
        this.editRestaurantData = {
            ...this.editRestaurantData,
            ...params
        };
    }

    /**
     * 双隧道切单隧道时，随隧道保存固定提交 ECCO 作为营业分析数据来源。
     * @param previousDatabasePort 切换前的 database_port
     * @returns 双转单时为 ECCO，非双转单时为 undefined
     */
    getCloudAnalysisOriginForDualToSingleSwitch(previousDatabasePort: DatabasePortValue): 'ECCO' | undefined {
        if (!this.isDatabasePortArray(previousDatabasePort)) {
            return undefined;
        }
        return 'ECCO';
    }

    /**
     * 校验双端口下载所需的端口信息。
     * @param databasePortItems 双端口数组
     * @returns 是否通过校验
     */
    validateMultiDatabasePortForDownload(databasePortItems: DatabasePortItem[]): boolean {
        return this.validateDatabasePortForPersist(databasePortItems, false);
    }

    /**
     * 校验保存或下载时的端口值；双端口保存允许异常数组中的空字符串继续透传。
     * @param databasePort 当前端口值
     * @param allowEmptyArrayPort 双端口模式下是否允许空字符串
     * @returns 是否通过校验
     */
    validateDatabasePortForPersist(databasePort: Restaurant['database_port'] | string | undefined, allowEmptyArrayPort: boolean): boolean {
        if (this.isDatabasePortArray(databasePort)) {
            const normalizedItems = this.normalizeDatabasePortValue(databasePort) as DatabasePortItem[];
            const validPortTexts: string[] = [];
            const hasInvalidPort = normalizedItems.some((item) => {
                const portText = String(item.port || '').trim();
                const passed = portText === ''
                    ? allowEmptyArrayPort
                    : BASE_CHECK_TYPE['maxNumberSix'].regular.test(portText);
                if (!passed) {
                    GlobalNotification.error(`${item.project} ${i18n.t('restaurant_page_input_text_restaurant_port')} ${i18n.t(BASE_CHECK_TYPE['maxNumberSix'].helperText, { x: 0, y: 6 })}`);
                    return true;
                }
                if (portText) {
                    validPortTexts.push(portText);
                }
                return false;
            });
            if (hasInvalidPort) {
                return false;
            }
            if (validPortTexts.length >= 2 && new Set(validPortTexts).size !== validPortTexts.length) {
                GlobalNotification.error('ECCO 与 EFR 端口不能相同');
                return false;
            }
            return true;
        }
        const portText = String(databasePort || '').trim();
        if (!portText) {
            return true;
        }
        const passed = BASE_CHECK_TYPE['maxNumberSix'].regular.test(portText);
        if (!passed) {
            GlobalNotification.error(`${i18n.t('restaurant_page_input_text_restaurant_port')} ${i18n.t(BASE_CHECK_TYPE['maxNumberSix'].helperText, { x: 0, y: 6 })}`);
        }
        return passed;
    }

    /**
     * 清理餐厅地址类字段首尾空格，用于保存前校验和接口提交。
     * @param data 当前餐厅表单数据
     * @returns 仅地址字段被 trim 的餐厅数据副本
     */
    trimRestaurantAddressFields(data: Restaurant): Restaurant {
        const trimmedData: Restaurant = { ...data };
        RESTAURANT_ADDRESS_TRIM_KEYS.forEach((key) => {
            // 字段值只有字符串时才清理，避免影响数组、布尔值等其他表单类型。
            const value = trimmedData[key];
            if (typeof value === 'string') {
                (trimmedData as Record<string, unknown>)[key] = value.trim();
            }
        });
        return trimmedData;
    }

    // 编辑餐厅信息
    updateEditRestaurantData(val: number | string | string[] | boolean | DatabasePortItem[], key: string, once?: boolean) {
        let cur = val;
        const origin = this.restaurantInfo[key as keyof Restaurant];
        if (['discount_annual', 'discount_info'].includes(key)) {
            if (val && Number(val) < 0) {
                cur = 0
            } else if (val) {
                cur = Number(Number(val).toFixed(2))
            }
        }
        // 判断源数据与新输入数据是否相等
        if (!_.isEqual(origin, cur)) {
            this.operateMap.set(key, { origin, current: cur })
        } else this.operateMap.delete(key);
        const editRestaurantData = _.set(this.editRestaurantData, key, cur);
        this.editRestaurantData = editRestaurantData;
        // 模块折扣和年费折扣，两个字段都是空的时候,输入其中任何一个字段,另一个字段就复制这个字段的值
        if (!once) {
            this.handleModuleAndYearDiscount(key, cur as string);
        }
    }

    // 处理模块折扣和年费折扣
    handleModuleAndYearDiscount(key: string, val: string) {
        if ((!this.restaurantInfo.discount_annual) && (!this.restaurantInfo.discount_info)) {
            if (key === 'discount_info') {
                this.isEditModuleDiscount = !!val;
                if (!this.isEditYearDiscount) {
                    this.updateEditRestaurantData(val, 'discount_annual', true);
                }
            } else if (key === 'discount_annual') {
                this.isEditYearDiscount = !!val;
                if (!this.isEditModuleDiscount) {
                    this.updateEditRestaurantData(val, 'discount_info', true);
                }
            }
        }
    }


    // 清空表单数据
    clearFormInfo() {
        this.editRestaurantData = {};
        // 清空编辑操作数据
        this.operateMap = new Map();
    }

    // 保存餐厅基础信息
    async onSave(isEditLegalPersonParams?: boolean) {
        // 修改reg_fiscal_code、reg_vat_number、reg_pec的必填和正则校验规则
        const isItaly = this.editRestaurantData.country_code === '+39';
        // 西班牙
        const isSpain = this.editRestaurantData.country_code === '+34';
        // 区域
        RestaurantInfoCardConfig.forEach((item) => {
            item?.children?.forEach((childrenItem) => {
                if (['reg_fiscal_code'].includes(childrenItem.key as string)) {
                    // 只有意大利是必填
                    childrenItem.required = isItaly ? true : false;
                    // 不同的国家使用不同的正则
                    childrenItem.regularType = isItaly ? 'vatNumber' : undefined;
                }
                if (['reg_vat_number'].includes(childrenItem.key as string)) {
                    // 只有意大利、西班牙是必填
                    childrenItem.required = (isItaly || isSpain) ? true : false;
                    // 不同的国家使用不同的正则
                    childrenItem.regularType = COUNTRY_VAT_REGULAR[this.editRestaurantData.country_code || ''];
                }
                if (childrenItem.key === 'reg_pec') {
                    // 只有意大利是必填
                    childrenItem.required = isItaly ? true : false;
                }
                if (['reg_city', 'reg_local'].includes(childrenItem.key as string)) {
                    // 不同的国家使用不同的正则
                    childrenItem.regularType = COUNTRY_REG_CITY_REGULAR[this.editRestaurantData.country_code || ''];
                }
                if (childrenItem.key === 'reg_postcode') {
                    // 不同的国家使用不同的正则
                    childrenItem.regularType = COUNTRY_REG_POSTCODE_REGULAR[this.editRestaurantData.country_code || ''];
                }
                if (childrenItem.key === 'reg_name') {
                    // 不同的国家使用不同的正则
                    childrenItem.regularType = COUNTRY_REG_NAME_REGULAR[this.editRestaurantData.country_code || ''];
                }
            })
        })
        // 双端口模式下忽略旧版单端口字段校验，改由后面的数组校验接管。
        const ignoreAllKeys = this.isDatabasePortArray(this.editRestaurantData.database_port)
            ? ['database_port']
            : undefined;
        // 保存前地址字段归一化副本，避免用户粘贴末尾空格导致校验或后端报错。
        const normalizedRestaurantData = this.trimRestaurantAddressFields(this.editRestaurantData);
        // 检查必填
        const errorList = checkCardFromRule({
            config: RestaurantInfoCardConfig,
            data: normalizedRestaurantData as {},
            ignoreAllKeys
        });
        this.updateAllowNullVerification(true);
        if (errorList?.length) {
            return
        }
        if (this.isDatabasePortArray(this.editRestaurantData.database_port)
            && !this.validateDatabasePortForPersist(this.editRestaurantData.database_port, true)) {
            return;
        }
        if (!isEditLegalPersonParams && this.isEditLegalPerson) {
            GlobalNotification.error(i18n.t('warrant_page_text_please_complete_step_before_proceeding', { step: i18n.t('warrant_page_text_generate_agreement') }))
            return

        }
        // this.updateAllowNullVerification(false);
        const params: Restaurant = {
            ...normalizedRestaurantData,
            // status：营业状态为歇业/暂停/SOSRESO/已取消时传 false（与后端约定一致）
            status: !['2', '5', '7', '8'].includes(this.editRestaurantData?.setting_restaurant_operating_status || '1'),
            discount_annual: String(this.editRestaurantData?.discount_annual),
            discount_info: String(this.editRestaurantData?.discount_info),
            authorization_type: this.editRestaurantData?.authorization_type === '' ? undefined : this.editRestaurantData?.authorization_type,
            // 数据隧道
            database_host: this.editRestaurantData?.database_host?.trim(),
            database_port: this.formatDatabasePortForSave(this.editRestaurantData?.database_port),
            // 私钥
            private_key: undefined,
            // 经销商：为空的默认为 SW总部直营
            belonged_reseller: this.editRestaurantData?.belonged_reseller === 'Admin' ? '' : this.editRestaurantData?.belonged_reseller
        }
        let result: any;
        if (this.componentType === 'ADD') {
            // params.private_key = `${0}${this.groupId}${''},${this.editRestaurantData.nickname}`
            result = await apiManage.create_restaurant(params);
        } else if (this.componentType === 'EDIT' || this.componentType === 'BASE_INFO') {
            // 判断是否有私钥  有则不传私钥字段  没有就增加私钥
            if (this.editRestaurantData.private_key !== '') {
                params.private_key = undefined;
            } else {
                const groupCreateDate = this.restaurantInfo.create_date ? moment(this.restaurantInfo.create_date, 'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]').valueOf() : 0;
                params.private_key = `${groupCreateDate}${this.groupId}${this.restaurantInfo.string_id || ''},${this.editRestaurantData.nickname}`
            }
            result = await apiManage.patch_restaurant_info({
                ...params,
                isv_fee: 10,
            }, this.restaurantId);
        }
        if (!result?.error) {
            // 上传餐厅头像
            if (this.editRestaurantDataAvatar.file) {
                const newRestaurantId = this.componentType === 'ADD' ? _.get(result, 'insertedId', '') : this.restaurantId
                await apiManage.upload_restaurant_image_admin(
                    newRestaurantId,
                    this.editRestaurantDataAvatar.file
                );
            }
            // 更改员工绑定集团
            if (this.groupId !== this.editRestaurantData.belonged_group) {
                const _restaurantId = this.componentType === 'ADD' ? result.insertedId
                    : this.restaurantId;
                await apiManage.patch_staff_belonged_gruop(_restaurantId);
                this.groupId = this.editRestaurantData.belonged_group;
            }
            // 消息提示语
            const message = this.componentType === 'ADD' ?
                i18n.t('restaurant_page_request_text_the_creation_of_the_restaurant_was_successful')
                : i18n.t('restaurant_page_request_text_the_change_of_restaurant_information_was_successful');
            GlobalNotification.success(message);
            // 新增成功,清空表单
            if (this.componentType === 'ADD') {
                this.initEditRestaurantData()
            } else {
                // 清空编辑操作数据
                this.operateMap = new Map();
            }

            return true
        } else {
            // 消息提示语
            let message = this.componentType === 'ADD' ?
                i18n.t('restaurant_page_request_text_the_creation_of_the_restaurant_was_failed')
                : i18n.t('restaurant_page_request_text_the_change_of_restaurant_information_was_failed');
            const details = result?.result?.details;
            message = typeof details === 'string' ? details : message;
            GlobalNotification.error(message);
            return false
        }
    }

    /**
     * 编辑时自动保存隧道信息
     * @param extraParams 随隧道一并提交的额外餐厅字段（如双转单时的 cloud_analysis_origin）
     */
    async onSaveTune(extraParams?: Pick<Restaurant, 'cloud_analysis_origin'>) {
        if (this.componentType === 'EDIT') {
            let qop_params = {};
            const { database_host, database_port, head, url, port } = this.editRestaurantData;
            const shouldValidateDatabasePort = this.isDatabasePortArray(database_port) || String(database_port || '').trim() !== '';
            if (shouldValidateDatabasePort && !this.validateDatabasePortForPersist(database_port, true)) {
                return false;
            }
            // 有权限
            if (this.QOP_WAN_WARRANT) {
                qop_params = {
                    // qop WAN端口地址
                    head,
                    // qop url 域名地址
                    url,
                    // qop port 端口号
                    port
                }
            }
            const params: Restaurant = {
                // 数据隧道
                database_host: database_host?.trim(),
                database_port: this.formatDatabasePortForSave(database_port),
                ...qop_params,
                ...extraParams,
            }
            const result = await apiManage.patch_restaurant_info({
                ...params,
            }, this.restaurantId);

            if (result && !result?.error) {
                this.syncTunnelSnapshot({
                    database_host: params.database_host,
                    database_port: params.database_port,
                    head,
                    url,
                    port,
                    ...(extraParams?.cloud_analysis_origin
                        ? { cloud_analysis_origin: extraParams.cloud_analysis_origin }
                        : {}),
                });
                GlobalNotification.success(i18n.t('restaurant_page_request_text_the_change_of_restaurant_information_was_successful'));
                return true;
            } else {
                GlobalNotification.error(i18n.t('restaurant_page_request_text_the_change_of_restaurant_information_was_failed'));
                return false;
            }
        }
        return false;
    }
    // 上传图片 更改头像文件
    async onChangeAvatar(files: File) {
        /**
         * 1.校验是否为图片以及是否存在
         * 2.对File类型转为url
         */
        const file = files;
        if (!file || !BASE_CHECK_TYPE['imageCheck'].regular.test(file?.type)) {
            GlobalNotification.error(`${i18n.t('general_message_image_type_incorrect_upload_failed')}`);
            return;
        } else {
            // 转为url链接
            const url = window.URL.createObjectURL(file);
            this.editRestaurantDataAvatar = {
                src: url,
                file
            };
        }
    }

    // 下载文件
    async downloadFile() {
        /**
         * 1.判断数据是否存在，以及是否合法
         */
        const { database_host, database_port, city, nickname, string_id, head, url, port } = this.editRestaurantData;
        if (!database_host || !BASE_CHECK_TYPE['description'].regular.test(database_host)) {
            GlobalNotification.error(`${i18n.t('restaurant_page_input_text_restaurant_host')} ${i18n.t(BASE_CHECK_TYPE['description'].helperText, { x: 0, y: 255 })}`);
            return;
        } else if (nickname === '') {
            GlobalNotification.error(`${i18n.t('restaurant_info_page_text_restaurant_name')} ${i18n.t('input_rule_text_required')}`);
            return;
        } else if (city === '') {
            GlobalNotification.error(`${i18n.t('restaurant_info_page_text_city')} ${i18n.t('input_rule_text_required')}`);
            // 校验城市必填控制
            this.validateCityRequired = true;
            // 滚动到对应城市所在表单位置
            cardFromScrollToView('address')
            // 双端口模式下忽略旧版单端口 database_port 的表单规则，避免数组被 maxNumberSix 误判（与 onSave 一致）
            const ignoreAllKeysForCityCheck = this.isDatabasePortArray(this.editRestaurantData.database_port)
                ? ['database_port']
                : undefined;
            // 检查必填  这里主要检查城市字段必填  因为下载隧道文件  需要城市信息
            checkCardFromRule({
                config: RestaurantInfoCardConfig,
                data: this.editRestaurantData as {},
                ignoreAllKeys: ignoreAllKeysForCityCheck
            });
            // 空校验
            this.updateAllowNullVerification(true);
            return;
        }
        const databasePortPayload = this.formatDatabasePortForSave(database_port);
        if (this.isDatabasePortArray(databasePortPayload)) {
            if (!this.validateMultiDatabasePortForDownload(databasePortPayload)) {
                return;
            }
            databasePortPayload.forEach((item) => {
                // EFR 项目的 ini 内容里，餐厅码需要加上 EFR_ 前缀。
                const tunnelStringId = item.project === 'EFR' ? `EFR_${string_id || ''}` : (string_id || '');
                generateText(`${item.project}_CLOUD_${string_id || ''}_frpc.ini`, getTextTemplate({
                    host: database_host,
                    port: String(item.port || ''),
                    city: city || '',
                    nickname: nickname || '',
                    string_id: tunnelStringId
                }))
            });
        } else if (!databasePortPayload || !BASE_CHECK_TYPE['maxNumberSix'].regular.test(String(databasePortPayload))) {
            GlobalNotification.error(`${i18n.t('restaurant_page_input_text_restaurant_port')} ${i18n.t(BASE_CHECK_TYPE['maxNumberSix'].helperText, { x: 0, y: 6 })}`);
            return;
        } else {
            generateText(`CLOUD_${string_id || ''}_frpc.ini`, getTextTemplate({
                host: database_host,
                port: String(databasePortPayload || ''),
                city: city || '',
                nickname: nickname || '',
                string_id: string_id || ''
            }))
        }
        // 有权限
        if (this.QOP_WAN_WARRANT) {
            const sid = string_id || '';
            generateText(`QOP_${sid}_frpc.toml`, getTextTemplateQOP({
                string_id: sid,
                qop_params: {
                    head,
                    url,
                    port
                }
            }));
            generateText(`QOP_EFR_${sid}_frpc.toml`, getTextTemplateQOPEFR({
                string_id: sid,
                qop_params: {
                    head,
                    url,
                    port
                }
            }));
        }
    }


    // 生成核心文件
    async getCoreFileAasKey() {
        // todo 这个mac后端还没处理好

        const params = {
            restaurant_string_id: this.restaurantInfo.string_id || '',
            decryptedDeviceMac: ''
        }
        const result = await apiManage.post_core_restaurant_aes_key(params);

        if (!result?.error) {
            generateText('key.swkey', result.file.aesKey, 'text/plain;charset=utf-8');
            generateText('encrypted.sw', result.file.encryptedFile, 'text/plain;charset=utf-8');
        } else {
            GlobalNotification.error(i18n.t('restaurant_page_message_failed_generate_restaurant_core_file'));
        }


    }

    async getCoreFileRsaPrivateKey() {
        // todo 这个mac后端还没处理好
        const params = {
            restaurant_string_id: this.restaurantInfo.string_id || '',
            decryptedDeviceMac: ''
        }
        const result = await apiManage.get_condiment_groups_private_key(params);

        if (!result?.error) {
            generateText('private_pem.swkey', `${result}`, 'text/plain;charset=utf-8');
        } else {
            GlobalNotification.error(i18n.t('restaurant_page_message_failed_generate_restaurant_rsa_key'));
        }
    }

    /**
     * 判断当前餐厅是否已经配置了隧道值。
     * @returns 主机或端口存在有效内容时返回 true
     */
    hasConfiguredTunnel(): boolean {
        const databaseHost = this.editRestaurantData.database_host || this.restaurantInfo.database_host || '';
        const databasePort = this.editRestaurantData.database_port ?? this.restaurantInfo.database_port;
        if (String(databaseHost).trim() !== '') {
            return true;
        }
        if (this.isDatabasePortArray(databasePort)) {
            return databasePort.some((item) => String(item?.port ?? '').trim() !== '');
        }
        return String(databasePort ?? '').trim() !== '';
    }

    /**
     * 处理单隧道或双隧道生成按钮点击。
     * @param mode 目标隧道模式
     */
    async handleGenerateTunnel(mode: TunnelGenerationMode) {
        if (!this.hasConfiguredTunnel()) {
            await this.generateTunnel(mode);
            return;
        }

        this.updateConfirmDialog({
            visible: true,
            content: this.getTunnelGenerateConfirmContent(mode),
            handleClick: async () => {
                this.confirmDialogData?.onCancel?.();
                await this.generateTunnel(mode);
            }
        });
    }

    /**
     * 按当前隧道模式与目标模式返回二次确认文案。
     * @param mode 目标隧道模式
     * @returns 确认弹窗正文
     */
    getTunnelGenerateConfirmContent(mode: TunnelGenerationMode): string {
        const isCurrentDual = this.isDatabasePortArray(this.editRestaurantData.database_port)
            || this.isDatabasePortArray(this.restaurantInfo.database_port);
        if (mode === 'single') {
            return i18n.t(isCurrentDual
                ? 'restaurant_page_text_switch_dual_to_single_tunnel_confirm'
                : 'restaurant_page_text_regenerate_single_tunnel_confirm');
        }
        return i18n.t(isCurrentDual
            ? 'restaurant_page_text_regenerate_dual_tunnel_confirm'
            : 'restaurant_page_text_switch_single_to_dual_tunnel_confirm');
    }

    /**
     * 按目标模式生成隧道配置。
     * @param mode 目标隧道模式
     */
    async generateTunnel(mode: TunnelGenerationMode) {
        if (mode === 'multi') {
            await this.fillDefaultMultiTunnelPorts(this.isDatabasePortArray(this.editRestaurantData.database_port));
            return;
        }
        await this.fillDefaultSingleTunnel();
    }

    /**
     * 自动填充单隧道配置，保留旧方法供现有调用方使用。
     */
    async autoFillTune() {
        await this.fillDefaultSingleTunnel();
    }

    /**
     * 生成单隧道配置；从双隧道切换时保留 ECCO 端口。
     */
    async fillDefaultSingleTunnel() {
        const previousDatabaseHost = this.editRestaurantData.database_host || '';
        const previousDatabasePort = this.editRestaurantData.database_port || '' as unknown as DatabasePortValue;
        const previousUrl = this.editRestaurantData.url || '';
        // 双转单时固定随隧道保存提交 cloud_analysis_origin: ECCO
        const previousCloudAnalysisOrigin = this.restaurantInfo.cloud_analysis_origin;
        const nextCloudAnalysisOrigin = this.getCloudAnalysisOriginForDualToSingleSwitch(previousDatabasePort);
        const currentDatabasePort = this.editRestaurantData.database_port;
        let nextDatabaseHost = '';
        let nextDatabasePort: string | number = '';

        if (this.isDatabasePortArray(currentDatabasePort)) {
            const eccoItem = currentDatabasePort.find((item) => item.project === 'ECCO');
            nextDatabaseHost = this.editRestaurantData.database_host || '';
            nextDatabasePort = this.normalizeDatabasePortItemPort(eccoItem?.port);
        } else {
            let result;
            try {
                result = await apiManage.get_restaurant_tunnel_default();
            } catch {
                GlobalNotification.error(i18n.t('restaurant_page_request_text_the_change_of_restaurant_information_was_failed'));
                return;
            }
            if (_.get(result, 'error')) {
                GlobalNotification.error(i18n.t('restaurant_page_request_text_the_change_of_restaurant_information_was_failed'));
                return;
            }
            nextDatabaseHost = result.database_host || '';
            nextDatabasePort = result.database_port || '';
        }

        this.updateEditRestaurantData(nextDatabaseHost, 'database_host');
        this.updateEditRestaurantData(nextDatabasePort, 'database_port');
        if (this.restaurantInfo.string_id) {
            this.updateEditRestaurantData(defaultQopWanConfig({ type: 'url', string_id: this.restaurantInfo.string_id }), 'url');
        }
        if (this.componentType === 'EDIT') {
            let saved = false;
            try {
                saved = await this.onSaveTune(
                    nextCloudAnalysisOrigin ? { cloud_analysis_origin: nextCloudAnalysisOrigin } : undefined
                );
            } catch {
                GlobalNotification.error(i18n.t('restaurant_page_request_text_the_change_of_restaurant_information_was_failed'));
            }
            if (!saved) {
                this.updateEditRestaurantData(previousDatabaseHost, 'database_host');
                this.updateEditRestaurantData(previousDatabasePort, 'database_port');
                this.updateEditRestaurantData(previousUrl, 'url');
                if (nextCloudAnalysisOrigin) {
                    this.restaurantInfo = {
                        ...this.restaurantInfo,
                        cloud_analysis_origin: previousCloudAnalysisOrigin,
                    };
                }
            }
        }
    }

    /**
     * 自动填充双隧道配置，保留旧方法供现有调用方使用。
     */
    async autoFillMultiTune() {
        await this.fillDefaultMultiTunnelPorts(this.isDatabasePortArray(this.editRestaurantData.database_port));
    }

    /**
     * 调用默认隧道接口生成双端口配置。
     * @param forceOverwrite 是否强制覆盖已有双端口
     */
    async fillDefaultMultiTunnelPorts(forceOverwrite: boolean) {
        const previousDatabaseHost = this.editRestaurantData.database_host || '';
        const previousDatabasePort = this.editRestaurantData.database_port || '' as unknown as DatabasePortValue;
        const currentDatabasePort = this.editRestaurantData.database_port;
        const hasLegacyDatabasePort = !forceOverwrite
            && !this.isDatabasePortArray(currentDatabasePort)
            && String(currentDatabasePort || '').trim() !== '';
        let firstDefaultTunnel;
        try {
            firstDefaultTunnel = await apiManage.get_restaurant_tunnel_default();
        } catch {
            GlobalNotification.error(i18n.t('restaurant_page_request_text_the_change_of_restaurant_information_was_failed'));
            return;
        }
        if (_.get(firstDefaultTunnel, 'error')) {
            GlobalNotification.error(i18n.t('restaurant_page_request_text_the_change_of_restaurant_information_was_failed'));
            return;
        }
        // 双端口模式只请求一次默认接口；单转双优先使用返回可用端口，若与 ECCO 撞号则加 1；全新双隧道或双隧道重生成时第二端口为默认端口加 1。
        const defaultPort = firstDefaultTunnel.database_port;
        const generatedEfrPort = defaultPort
            ? Number(defaultPort) + 1
            : '';
        let nextDatabasePort: DatabasePortItem[] = [];
        const nextDatabaseHost = firstDefaultTunnel.database_host || '';
        if (hasLegacyDatabasePort) {
            // 单转双：保留原单端口为 ECCO；EFR 使用默认可用端口，新增未落库时可能与 ECCO 相同，需顺延 +1。
            const eccoPort = this.normalizeDatabasePortItemPort(currentDatabasePort as string | number);
            const defaultPortNumber = Number(defaultPort);
            const eccoPortNumber = Number(eccoPort);
            const efrPort = defaultPort
                && (!Number.isFinite(eccoPortNumber) || defaultPortNumber !== eccoPortNumber)
                ? defaultPort
                : (Number.isFinite(eccoPortNumber) ? eccoPortNumber + 1 : generatedEfrPort);
            nextDatabasePort = [
                {
                    project: 'ECCO',
                    port: eccoPort
                },
                {
                    project: 'EFR',
                    port: efrPort
                }
            ];
        } else {
            nextDatabasePort = [
                {
                    project: 'ECCO',
                    port: defaultPort || ''
                },
                {
                    project: 'EFR',
                    port: generatedEfrPort
                }
            ];
        }
        this.updateEditRestaurantData(nextDatabaseHost, 'database_host');
        this.updateEditRestaurantData(nextDatabasePort, 'database_port');
        // 编辑态自动保存；新增态仅回填表单。
        if (this.componentType === 'EDIT') {
            let saved = false;
            try {
                saved = await this.onSaveTune();
            } catch {
                GlobalNotification.error(i18n.t('restaurant_page_request_text_the_change_of_restaurant_information_was_failed'));
            }
            if (!saved) {
                this.updateEditRestaurantData(previousDatabaseHost, 'database_host');
                this.updateEditRestaurantData(previousDatabasePort, 'database_port');
            }
        }
    }

    // 新增餐厅密钥
    async createSecretKey(params: { name: string }) {
        const string_id = this.restaurantInfo.string_id || '';
        const result = await apiManage.post_restaurant_string_id_secret_key(string_id, params.name);
        if (!result?.error) {
            this.getRestaurantInfo(this.restaurantId);
            this.updateExtensionsDialog({ open: false })
            GlobalNotification.success(`${i18n.t('general_text_create_success')}`);
        } else {
            GlobalNotification.error(`${i18n.t('general_text_create_failed')}`);
        }

    }

    // 刷新密钥
    async refreshSecretKey(_id: string) {
        this.updateConfirmDialog({ visible: false });
        const result = await apiManage.patch_restaurant_string_id_secret_key_refresh({
            string_id: this.restaurantInfo.string_id || '',
            _id,
        });
        if (!result?.error) {
            this.getRestaurantInfo(this.restaurantId);
            GlobalNotification.success(`${i18n.t('general_text_refresh_success')}`);
        } else {
            GlobalNotification.error(`${i18n.t('general_text_refresh_failed')}`);
        }
    }

    /**
     * 删除密钥
     */
    async deleteSecretKey(_id: string) {
        this.confirmDialogData?.onCancel?.();
        const result = await apiManage.delete_restaurant_string_id_secret_key({
            string_id: this.restaurantInfo.string_id || '',
            _id
        });
        if (!result?.error) {
            this.getRestaurantInfo(this.restaurantId);
            GlobalNotification.success(`${i18n.t('general_message_text_delete_successfully')}`);
        } else {
            GlobalNotification.error(`${i18n.t('general_message_text_delete_failed')}`);
        }
    }


    /**
     * 修改密钥名称
     */
    async editExtensions(params: { name: string }) {
        const result = await apiManage.patch_restaurant_string_id_secret_key({
            string_id: this.restaurantInfo.string_id || '',
            _id: this.showExtensionsDialog.secretKey._id || '',
            name: params.name
        });
        if (!result?.error) {
            this.getRestaurantInfo(this.restaurantId);
            this.updateExtensionsDialog({ open: false })
            GlobalNotification.success(`${i18n.t('general_text_modify_success')}`);
        } else {
            GlobalNotification.error(`${i18n.t('general_text_modify_failed')}`);
        }
    }

    /**
     * 更新私钥
     */
    async patchPrivateKey() {
        const groupCreateDate = this.restaurantInfo.create_date ? moment(this.restaurantInfo.create_date, 'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]').valueOf() : 0;
        // 私钥
        const privateKey = `${groupCreateDate}${this.groupId}${this.restaurantInfo.string_id},${this.editRestaurantData.nickname}`;
        const result = await apiManage.patch_restaurant_private_key({
            private_key: privateKey,
            belonged_group: this.groupId,
            string_id: this.restaurantInfo.string_id || ''
        }, this.restaurantId)
        return result
    }
    /**
     * 修复私钥错误问题
     * 修复1.0.18以下版本“更改所属集团”导致集团数据错误数据同步失败的问题
     */
    async handleFixPrivateKey(): Promise<void> {
        const result = await this.patchPrivateKey()
        // 请求成功
        if (!result?.error) {
            const nModified = result?.result?.nModified;
            let message = '';
            if (nModified === 0) {
                // 当前餐馆集团数据无错误，无需修复
                message = i18n.t('restaurant_info_page_text_result_fix_no_error')
            } else if (nModified === 1) {
                // 当前餐馆集团错误问题已修复：私钥已更新
                message = i18n.t('restaurant_info_page_text_result_fix_private_key_error')
            } else {
                // 当前餐馆集团错误问题已修复：私钥已更新,扩展功能秘钥已删除,请重新生成扩展功能密钥,并在RBP中更新密钥
                message = i18n.t('restaurant_info_page_text_result_fix_key_error')
            }
            GlobalNotification.success(message)
            this.getRestaurantInfo(this.restaurantId);
        } else {
            // 服务异常
            GlobalNotification.error(i18n.t('settings_page_msg_server_exception'))
        }
    }

    /**
     * 下载密钥文件.txt
     */
    downloadKeyFile(keyInfo: SecretKey) {
        const { string_id, nickname, city } = this.restaurantInfo;
        const fileName = `${string_id}_${nickname}_${city}.txt`;
        const time = keyInfo?.creation_time ? moment(keyInfo.creation_time, moment.HTML5_FMT.DATETIME_LOCAL_MS).format('HH:mm:ss DD/MM/YYYY') : '';
        const content = `-${nickname}_${city}\n-${time}\n-${keyInfo.value}`;
        generateText(fileName, `${content}`, 'text/plain;charset=utf-8');

    }
    /**
     * 保存GYB
     */
    async onSaveGYB(error: boolean) {
        if (!error) {
            this.getRestaurantGYB(this.restaurantId);
            this.showGYBDialog?.onClose();
        }
    }

    // 设置为默认设备
    async handleDefaultSetting(GybDevice: GybDevice) {
        const params = {
            string_id: this.restaurantInfo.string_id || '',
            device_code: GybDevice.device_code || ''
        }
        this.confirmDialogData?.onCancel?.();
        const result = await apiManage.patch_restaurant_string_id_gyb_device_default(params);
        if (!result?.error) {
            this.getRestaurantGYB(this.restaurantId);
            GlobalNotification.success(i18n.t('gyb_page_text_set_successfully'));
        } else {
            GlobalNotification.error(i18n.t('gyb_page_text_set_failed'));
        }
    }

    // 更新GYB设备
    async handleUpdate(GybDevice: GybDevice) {
        if (GybDevice._id) {
            const param = {
                brand: GybDevice.brand || '',
                dealer: GybDevice.dealer || '',
                deviceCode: GybDevice.device_code || '',
                licenseCode: GybDevice.license_code || '',
                local: GybDevice.local || '',
                locationId: GybDevice.location_id || '',
                model: GybDevice.model || '',
                name: GybDevice.name || '',
                restaurant: GybDevice.restaurant_id || '',
                refresh: false
            }
            const result = await apiManage.bind_gyb_register(param);
            if (!result?.error) {
                this.getRestaurantGYB(this.restaurantId);
                GlobalNotification.success(i18n.t('general_text_refresh_success'));
            } else {
                if (result?.result?.details) {
                    GlobalNotification.error(`${result?.result?.details}`);
                } else {
                    GlobalNotification.error(i18n.t('general_text_refresh_failed'));
                }
            }
        }
    }

    // 删除
    async GYBhandleDelete(GybDevice: GybDevice) {
        if (GybDevice._id) {
            this.confirmDialogData?.onCancel?.();
            // 删除GYB
            const result = await apiManage.delete_gyb_device(GybDevice?._id);
            if (!result?.error) {
                this.getRestaurantGYB(this.restaurantId);
                GlobalNotification.success(i18n.t('gyb_page_text_the_removal_of_the_GYB_device_was_successful'));
            } else {
                GlobalNotification.error(i18n.t('gyb_page_text_removing_the_GYB_device_failed'));
            }
        }
    }
    // 设置负责人信息弹窗数据
    updateResponsabileDialog(data: typeof this.responsabileDialogData) {
        this.responsabileDialogData = {
            visible: data.visible,
            title: data.title,
            onClose: () => { return this.updateResponsabileDialog({ ...this.responsabileDialogData, visible: false }) }
        };
    }
    // 解绑餐厅负责人
    async unbind(staff: Staff) {
        this.confirmDialogData.onCancel?.();
        // 解绑餐厅负责人
        const params = {
            _id: staff?._id || '',
            belonged_restaurant: [this.restaurantInfo.string_id],
            type: 'REMOVE'
        }
        const result = await apiManage.patch_staff_id_belonged_restaurant(params);
        if (!result?.error) {
            GlobalNotification.success(
                i18n.t('restaurant_page_request_text_untie_restaurant_association_successfully')
            );
            this.getRestaurantInfo();
        } else {
            GlobalNotification.error(
                i18n.t('restaurant_page_request_text_failed_unbind_restaurant_association')
            );
        }
    }


    /**
     * 获取餐厅信息 fiskaly 凭据
     * 1、创建管理组织  2、通过已有的管理组织创建apikeysecret  3、创建纳税人并生成协议and上传到OSS
     * @param processingMode
     */
    async getFiskalyCredentialsStepOneTwoThree(processingMode: string) {
        // 先判断前面的步骤是否已执行
        const isCompleted = this.checkFiskalyStepCompleted(Number(processingMode));
        if (!isCompleted) {
            return;
        }

        const params = this.trimRestaurantAddressFields(_.pick(this.editRestaurantData, [
            'reg_name',
            'reg_local',
            'reg_postcode',
            'reg_vat_number',
            'reg_city',
            'reg_country',
            'reg_municipality',
            'reg_street_number',
            'reg_territory'
        ]));

        // params是否全部不为空，要具体提示哪个值不能为空
        const emptyFields = Object.entries(params).filter(([, value]) => !value);

        if (emptyFields.length > 0) {
            const fieldName = emptyFields[0][0];
            GlobalNotification.error(`${i18n.t('input_rule_text_required')} ${this.getFieldI18nName(fieldName)}`);
            return;
        }
        // 国家区号必须是ES
        if (this.editRestaurantData.country_code !== '+34') {
            GlobalNotification.error(`${i18n.t('general_text_country_calling_code_must_be_34_text')}`);
            return;
        }
        // reg_country必须是ES
        if (params.reg_country !== 'ES') {
            GlobalNotification.error(`${i18n.t('general_text_country_must_be_spain_text')}`);
            return;
        }
        // 表单有修改 先保存
        if (this.operateMap.size > 0) {
            const isSave = await this.onSave();
            if (!isSave) {
                return;
            } else {
                this.getRestaurantInfo(this.restaurantId);
            }
        } else {
            // reg_vat_number必须是有效的VAT号码 
            if (!BASE_CHECK_TYPE['spainVatNumber'].regular.test(params.reg_vat_number || '')) {
                GlobalNotification.error(`${i18n.t('general_text_spain_vat_number_invalid_format_text')}`);
                return;
            }
        }

        // reg_state 不在 pick 列表内，单独取 trim 后的值，避免州/省字段带首尾空格。
        const trimmedRegState = typeof this.editRestaurantData.reg_state === 'string'
            ? this.editRestaurantData.reg_state.trim()
            : this.editRestaurantData.reg_state;
        const result = await apiManage.post_fiskaly_register(this.restaurantInfo.string_id || '', {
            ...params,
            reg_pec: processingMode === '3' && this.editRestaurantData.reg_pec ? this.editRestaurantData.reg_pec : undefined,
            reg_state: trimmedRegState || undefined,
            processing_mode: processingMode,
            fiskaly_environment: this.editRestaurantData.fiskaly_environment

        });
        if (result && !result?.error) {
            await this.getRestaurantInfo(this.restaurantId);
            GlobalNotification.success(`${i18n.t('global_text_operate_success')}`);
        } else {
            const details = result?.result?.details;
            const message = typeof details === 'string' ? details : result?.result?.message;
            const msg = typeof message === 'string' ? message : i18n.t('global_text_operate_failure');
            GlobalNotification.error(msg);
        }
    }
    /**
     * 获取餐厅信息 fiskaly 凭据
     * 1、创建管理组织  2、通过已有的管理组织创建apikeysecret  3、创建纳税人并生成协议and上传到OSS
     * @param processingMode
     */
    async getFiskalyCredentialsStepFour(processingMode: string) {
        // 先判断前面的步骤是否已执行
        const isCompleted = this.checkFiskalyStepCompleted(Number(processingMode));
        if (!isCompleted) {
            return;
        }

        const params =
            this.trimRestaurantAddressFields(_.pick(this.editRestaurantData, [
                'corp_name',
                'corp_vat_number',
                'corp_local',
                'corp_postcode',
                'corp_city',
                'corp_state',
                'corp_country',
                'corp_municipality',
                'corp_street_number'
            ]));

        // params是否全部不为空，要具体提示哪个值不能为空
        const emptyFields = Object.entries(params).filter(([, value]) => !value);

        if (emptyFields.length > 0) {
            const fieldName = emptyFields[0][0];
            GlobalNotification.error(`${i18n.t('input_rule_text_required')} ${this.getFieldI18nName(fieldName)}`);
            return;
        }
        // 国家区号必须是ES
        if (this.editRestaurantData.country_code !== '+34') {
            GlobalNotification.error(`${i18n.t('general_text_country_calling_code_must_be_34_text')}`);
            return;
        }

        // reg_country必须是ES
        if (params.corp_country !== 'ES') {
            GlobalNotification.error(`${i18n.t('general_text_country_must_be_spain_text')}`);
            return;
        }


        // 表单有修改 先保存
        if (this.operateMap.size > 0) {
            const isSave = await this.onSave(true);
            if (!isSave) {
                return;
            }
        } else {
            // reg_vat_number必须是有效的VAT号码 
            if (!BASE_CHECK_TYPE['spainVatNumber'].regular.test(params.corp_vat_number || '')) {
                GlobalNotification.error(`${i18n.t('general_text_spain_vat_number_invalid_format_text')}`);
                return;
            }
        }


        const result = await apiManage.post_fiskaly_register(this.restaurantInfo.string_id || '', {
            ...params,
            processing_mode: processingMode,
            fiskaly_environment: this.editRestaurantData.fiskaly_environment

        });
        if (result && !result?.error) {
            await this.getRestaurantInfo(this.restaurantId);
            GlobalNotification.success(`${i18n.t('global_text_operate_success')}`);
        } else {
            const details = result?.result?.details;
            const message = typeof details === 'string' ? details : result?.result?.message;
            const msg = typeof message === 'string' ? message : i18n.t('global_text_operate_failure');
            GlobalNotification.error(msg);
        }
        this.isEditLegalPerson = false
    }

    // 校验前面的步骤是否已完成
    checkFiskalyStepCompleted(step: number): boolean {
        // step 1: 创建管理组织
        if (step >= 2) {
            if (!this.restaurantInfo.fiskaly_manage_organization_id) {
                GlobalNotification.error(i18n.t('warrant_page_text_please_complete_step_before_proceeding', { step: `1. ${i18n.t('warrant_page_text_register_organization')}` }));
                return false;
            }
        }
        // step 2: 通过已有的管理组织创建apikeysecret
        if (step >= 3) {
            if (!this.restaurantInfo.fiskaly_manage_organization_key || !this.restaurantInfo.fiskaly_manage_organization_secret) {
                GlobalNotification.error(i18n.t('warrant_page_text_please_complete_step_before_proceeding', { step: `2. ${i18n.t('warrant_page_text_generate_voucher')}` }));
                return false;
            }
        }
        // step 3: 创建纳税人并生成协议and上传到OSS
        if (step >= 4) {
            if (!this.restaurantInfo.taxpayer_status) {
                GlobalNotification.error(i18n.t('warrant_page_text_please_complete_step_before_proceeding', { step: `3. ${i18n.t('warrant_page_text_register_taxpayer')}` }));
                return false;
            }
        }
        return true;
    }

    // Fiskaly 后台权限选择框onChange回调
    async inviteFiskalyChange(value: boolean) {
        if (value) {
            await this.inviteFiskaly();
        } else {
            await this.unbindFiskaly();
        }
    }


    /**
     * Fiskaly 邀请 fiskaly_manage_organization_user
     */
    async inviteFiskaly() {
        if (!this.editRestaurantData.reg_pec) {
            GlobalNotification.error(`${i18n.t('input_rule_text_required')} ${this.getFieldI18nName('reg_pec')}`);
            return
        }
        // 表单有修改 先保存
        if (this.operateMap.size > 0) {
            const isSave = await this.onSave();
            if (!isSave) {
                return;
            }
        }

        const result = await apiManage.patch_fiskaly_manage_organization_user(this.restaurantInfo.string_id || '', {
            fiskaly_manage_organization_id: this.restaurantInfo.fiskaly_manage_organization_id || '',
            reg_pec: this.editRestaurantData.reg_pec || '',
            // 1:邀请 2:删除
            operate_type: '1',
        })
        if (!result?.error) {
            await this.getRestaurantInfo(this.restaurantId);
            GlobalNotification.success(`${i18n.t('global_text_operate_success')}`);
        } else {
            GlobalNotification.error(`${i18n.t('global_text_operate_failure')}`);
        }
    }

    /**
     * Fiskaly 解绑 fiskaly_manage_organization_user
     */
    async unbindFiskaly() {
        const result = await apiManage.patch_fiskaly_manage_organization_user(this.restaurantInfo.string_id || '', {
            fiskaly_manage_organization_id: this.restaurantInfo.fiskaly_manage_organization_id || '',
            fiskaly_manage_organization_user_id: this.restaurantInfo.fiskaly_manage_organization_user_id || '',
            //  2:删除
            operate_type: '2',
        })
        if (!result?.error) {
            await this.getRestaurantInfo(this.restaurantId);
            GlobalNotification.success(`${i18n.t('global_text_operate_success')}`);
        } else {
            GlobalNotification.error(`${i18n.t('global_text_operate_failure')}`);
        }
    }

    // 字段对应的国际化名称
    getFieldI18nName(field: string): string {
        const allCards = RestaurantInfoCardConfig.reduce((acc, card) => {
            return acc?.concat(card.children || []);
        }, [] as typeof RestaurantInfoCardConfig[0]['children']);
        const fieldItem = allCards?.find((item) => item.key === field);
        if (fieldItem) {
            return i18n.t(fieldItem.label as string);
        }
        return '';
    }



    /**
     * 解除禁用
     */
    async unbindDisableTax() {
        const result = await apiManage.patch_restaurant_info({
            fiskaly_signer_status: 'DISABLED',
        }, this.restaurantId)
        if (!result?.error) {
            await this.getRestaurantInfo(this.restaurantId);
            GlobalNotification.success(`${i18n.t('global_text_operate_success')}`);
        } else {
            const details = result?.result?.details;
            const message = typeof details === 'string' ? details : i18n.t('global_text_operate_failure');
            GlobalNotification.error(message);
        }
        this.confirmDialogData?.onCancel?.();
    }

    /**
     * 修改客户端状态
     * @param current 
     * @param status 
     */
    async unbindDisableFiskalyClientInfo(item: FiskalyClientInfo, status: string) {
        const current = _.cloneDeep(item);
        current.status = status;
        const result = await apiManage.patch_restaurant_info({
            fiskaly_client_info: [current],
        }, this.restaurantId)
        if (!result?.error) {
            await this.getRestaurantInfo(this.restaurantId);
            GlobalNotification.success(`${i18n.t('global_text_operate_success')}`);
        } else {
            const details = result?.result?.details;
            const message = typeof details === 'string' ? details : i18n.t('global_text_operate_failure');
            GlobalNotification.error(message);
        }
        this.confirmDialogData?.onCancel?.();
    }

    // 根据邮编搜索市镇编码(INE)
    async searchMunicipalityByPostcode(postcode: 'corp_postcode' | 'reg_postcode', municipality: 'corp_municipality' | 'reg_municipality') {
        // 邮编不能为空
        if (!this.editRestaurantData[postcode]) {
            GlobalNotification.error(`${i18n.t('input_rule_text_required')} ${this.getFieldI18nName(postcode)}`);
            return;
        }
        const result = await apiManage.get_municipality_by_postcode(this.editRestaurantData[postcode] || '');
        if (!result?.error) {
            const CMUN_ARR = result?.data?.map((item) => item.CMUN);
            if (!CMUN_ARR?.length) {
                GlobalNotification.error(i18n.t('general_text_no_data'));
                return;
            }
            // CMUN_ARR去重
            const CMUN_ARR_UNIQUE = _.uniq(CMUN_ARR);
            this.editRestaurantData[municipality] = CMUN_ARR_UNIQUE[0];
        }
    }

    // 根据当前选择的国家，获取对应的语言
    getLanguage() {
        const countryCode = this.editRestaurantData.country_code;
        const ccTLD = this.countryCodeList.find((item) => item.value === countryCode)?.item?.ccTLD || 'IT';
        const value = getLanguageByCountry(ccTLD || 'IT');
        return value;
    }

    // 修改法人信息二次确认弹窗
    updateConfirmDialogLegalPerson() {
        this.updateConfirmDialog({
            visible: true,
            title: '',
            content: i18n.t('warrant_page_text_legal_entity_change_confirm'),
            handleClick: () => this.updateConfirmDialog({ visible: false }),
            onCancel: () => {
                this.updateConfirmDialog({ visible: false })
                this.isDisabledLegalPerson = false;
                this.isEditLegalPerson = true
            },
            onClose: () => this.updateConfirmDialog({ visible: false }),
            cancelText: i18n.t('warrant_page_text_continue_editing'),
            confirmText: i18n.t('general_button_cancel')
        })

    }


}
type COUNTRYTIMEZONE = {
    label: string;
    value: string;
    _id: string;
}
type Props = {
    componentType?: 'ADD' | 'EDIT' | 'PREVIEW' | 'BASE_INFO';
    restaurantId?: string;
    belonged_group?: string;
}


export interface IRestaurantInfoState {
    componentType: 'ADD' | 'EDIT' | 'PREVIEW' | 'BASE_INFO' | undefined;
}